import React, { useState, useCallback } from 'react';
import { BarChart3, Users, ShieldCheck, Brain } from 'lucide-react';
import toast from '../../../utils/toast';
import { useTabPersistence } from '../../../hooks/usePersistence';
import { talentAssessmentsAPI } from '../../../services/api';

import BigFiveAggregate from './BigFiveAggregate';
import AssessmentDirectory from './AssessmentDirectory';
import AssessmentAccessLog from './AssessmentAccessLog';
import IndividualResultModal from './IndividualResultModal';

/**
 * Talent Insights — HR Admin module shell.
 *
 * Two-level navigation:
 *   - Assessment switcher — picks the inventory every tab below operates
 *     on. Hidden while only one inventory (Big Five) is registered; it
 *     reappears automatically when a second inventory is added.
 *   - Tabs (Aggregate / Employee Directory / Access Log) — identical
 *     structure for every inventory; the directory and access log are
 *     shared inventory-agnostic components, while each inventory brings
 *     its own aggregate dashboard and result body in the modal.
 *
 * Designed to grow: appraisals, 360 feedback, engagement surveys, and
 * skills self-assessments slot in as new ASSESSMENTS entries.
 *
 * Individual-result reveal flow
 * -----------------------------
 *   When the user clicks "View result" in the directory, this shell makes
 *   an optimistic call to the result endpoint with NO justification body.
 *   The server then either:
 *     - Returns 200 with the result + an `audit` block — meaning the
 *       same viewer is still inside the 30-min grace window from a
 *       previous justified access for this employee. We open the modal
 *       directly to the result step with `preloadedResult`, and the
 *       modal surfaces "Reusing reason from <when>: <code>" so the user
 *       can see why no gate appeared.
 *     - Returns 400 with `code: 'justification_required'` — meaning no
 *       grace window is active. We open the modal to its gate step as
 *       before, and the user supplies a fresh justification which writes
 *       a new audit row.
 *
 *   The optimistic call costs one extra round-trip on the "no grace
 *   window" path, but eliminates re-prompting friction during a single
 *   work episode without weakening the audit log (the server is the
 *   sole arbiter of whether the window is still open — the client is
 *   never trusted to skip the gate on its own).
 */

const VALID_TABS = ['aggregate', 'employees', 'access-log'];
const VALID_ASSESSMENTS = ['big-five'];

const ASSESSMENTS = {
  'big-five': {
    label: 'Big Five Profile',
    icon: Brain,
    fetchIndividualResult: (staffId, payload, config) =>
      talentAssessmentsAPI.adminGetBigFiveIndividualResult(staffId, payload, config),
    fetchDirectory: talentAssessmentsAPI.adminGetBigFiveDirectory,
    fetchAccessLog: talentAssessmentsAPI.adminGetBigFiveAccessLog,
    directoryDescription: 'Submitted Big Five Profiles. Click to view an individual result.',
  },
};

const TalentInsights = () => {
  const [assessment, setAssessment] = useTabPersistence(
    'talent-insights-assessment', 'big-five', VALID_ASSESSMENTS,
  );
  const [activeTab, setActiveTab] = useTabPersistence('talent-insights', 'aggregate', VALID_TABS);
  const [individualTarget, setIndividualTarget] = useState(null);
  const [pendingStaffId, setPendingStaffId] = useState(null);
  // Lets the Aggregate tab's "small department → use Individual view"
  // callout route HR straight to the Employee Directory pre-filtered
  // to that one department. The directory's existing free-text search
  // covers department names, so we pre-fill its search box rather than
  // introducing a separate department-filter UI.
  const [directoryInitialSearch, setDirectoryInitialSearch] = useState('');

  const current = ASSESSMENTS[assessment] || ASSESSMENTS['big-five'];

  const handleSwitchAssessment = useCallback((id) => {
    setAssessment(id);
    // A modal or pre-filled drill-down from the other inventory would be
    // misleading once the context switches.
    setIndividualTarget(null);
    setDirectoryInitialSearch('');
  }, [setAssessment]);

  const handleDrillDownToDirectory = useCallback((segmentValue) => {
    setDirectoryInitialSearch(segmentValue || '');
    setActiveTab('employees');
  }, [setActiveTab]);

  const handleSelectEmployee = useCallback(async (emp) => {
    if (!emp || pendingStaffId) return;
    setPendingStaffId(emp.staff_id);
    try {
      // Optimistic call with no body. Server returns 200 if and only if
      // the grace window is open for this (viewer, employee) pair.
      // Mark 400 as an expected status so the global axios error logger
      // doesn't flag the `justification_required` control-flow response
      // as a red ❌ in the console — that path is the intended "open the
      // gate" signal, not a failure.
      const { data } = await current.fetchIndividualResult(
        emp.staff_id, {}, { expectedStatuses: [400] },
      );
      setIndividualTarget({ ...emp, _preloadedResult: data });
    } catch (err) {
      const code = err.response?.data?.code;
      if (err.response?.status === 400 && code === 'justification_required') {
        // Expected path — no grace window. Open the gate.
        setIndividualTarget(emp);
      } else if (err.response?.status === 404) {
        toast.error(`No ${current.label} result on file for this employee.`);
      } else {
        toast.error('Could not open this employee\'s result.');
      }
    } finally {
      setPendingStaffId(null);
    }
  }, [pendingStaffId, current]);

  const tabs = [
    {
      id: 'aggregate',
      name: 'Aggregate Dashboard',
      icon: BarChart3,
      description: 'Company-wide and segmented benchmarks (k≥5 anonymity).',
    },
    {
      id: 'employees',
      name: 'Employee Directory',
      icon: Users,
      description: current.directoryDescription,
    },
    {
      id: 'access-log',
      name: 'Access Log',
      icon: ShieldCheck,
      description: 'Audit trail of HR individual-result accesses.',
    },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'aggregate':
        return (
          <BigFiveAggregate
            onDrillDownToDirectory={handleDrillDownToDirectory}
          />
        );
      case 'employees':
        return (
          <AssessmentDirectory
            key={assessment}
            fetchDirectory={current.fetchDirectory}
            assessmentName={current.label}
            onSelectEmployee={handleSelectEmployee}
            pendingStaffId={pendingStaffId}
            initialSearch={directoryInitialSearch}
          />
        );
      case 'access-log':
        return (
          <AssessmentAccessLog
            key={assessment}
            fetchAccessLog={current.fetchAccessLog}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <IndividualResultModal
        target={individualTarget}
        onClose={() => setIndividualTarget(null)}
        assessment={assessment}
      />

      <div>
        {/* Module header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Talent Insights</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
            Psychometric assessments — aggregated benchmarks and audited individual access.
          </p>
        </div>

        {/* Assessment switcher — which inventory the tabs below operate on.
            Hidden while only one inventory is registered (nothing to switch). */}
        {VALID_ASSESSMENTS.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-3">
            {VALID_ASSESSMENTS.map((id) => {
              const a = ASSESSMENTS[id];
              const Icon = a.icon;
              const isActive = assessment === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSwitchAssessment(id)}
                  className={`tab-btn-admin ${isActive ? 'active' : ''}`}
                  aria-pressed={isActive}
                >
                  <Icon className="w-4 h-4" />
                  {a.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="card">
          <div className="card-content">
            {renderActiveTab()}
          </div>
        </div>
      </div>
    </>
  );
};

export default TalentInsights;
